import React, { useEffect, useRef, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";

/**
 * Address autocomplete using OpenStreetMap Nominatim (free, no API key).
 * Drop-in compatible with Google Places visually. Country-restricted to DE.
 */
export default function AddressAutocomplete({ value, onChange, placeholder, testid }) {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const search = async (q) => {
    if (q.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&countrycodes=de,nl,be&limit=6&accept-language=de&q=${encodeURIComponent(q)}`;
      const res = await fetch(url, { headers: { "Accept": "application/json" } });
      const data = await res.json();
      setSuggestions(Array.isArray(data) ? data : []);
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const onInput = (v) => {
    setQuery(v);
    onChange(v);
    setOpen(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 350);
  };

  const formatSuggestion = (s) => {
    const a = s.address || {};
    const street = [a.road, a.house_number].filter(Boolean).join(" ");
    const city = a.city || a.town || a.village || a.municipality || a.county || "";
    const post = a.postcode || "";
    const line = [street, [post, city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
    return line || s.display_name;
  };

  const pick = (s) => {
    const formatted = formatSuggestion(s);
    setQuery(formatted);
    onChange(formatted);
    setOpen(false);
    setSuggestions([]);
  };

  return (
    <div ref={boxRef} className="relative" data-testid={testid ? `${testid}-wrap` : undefined}>
      <div className="relative">
        <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          className="input"
          style={{ paddingLeft: "2.75rem" }}
          placeholder={placeholder}
          value={query}
          onChange={(e) => onInput(e.target.value)}
          onFocus={() => suggestions.length && setOpen(true)}
          autoComplete="off"
          data-testid={testid}
        />
        {loading && (
          <Loader2 className="animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
        )}
      </div>
      {open && suggestions.length > 0 && (
        <div
          className="absolute z-30 mt-1 w-full bg-white rounded-xl border border-slate-200 shadow-floating max-h-72 overflow-auto"
          data-testid={testid ? `${testid}-list` : undefined}
        >
          {suggestions.map((s) => (
            <button
              key={s.place_id}
              type="button"
              onClick={() => pick(s)}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-amber-50 border-b last:border-b-0 border-slate-100 flex items-start gap-2"
            >
              <MapPin size={14} className="text-slate-400 mt-0.5 shrink-0" />
              <span className="text-slate-800">{formatSuggestion(s)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
